/**
 * Etiquetas de los movimientos de stock — fuente única.
 *
 * Por qué vive acá (PROMPT_N°053): el mapa estaba dentro de
 * `TablaMovimientosStock.tsx`, y el Dashboard tenía otro propio que
 * etiquetaba **por `tipo` en vez de por `motivo`**. Resultado: una salida
 * de bodega aparecía como "Salida por producción" y un reverso como
 * "Entrada de stock". Con el kardex sumando una tercera pantalla, tres
 * copias del mismo mapa era garantía de que volvieran a divergir.
 *
 * `tipo` (entrada/salida/ajuste) sirve para el signo y el color. Lo que
 * dice QUÉ pasó es el `motivo`, y es lo que hay que mostrar.
 *
 * Está en `@suminuta/shared` y no en el frontend porque el export CSV del
 * kardex necesita las mismas etiquetas del lado del servidor: si el
 * archivo que baja el cliente dijera otra cosa que la pantalla, el
 * problema sería peor que el original.
 */
/** Los valores de `movimientos_stock.motivo`, en el orden del ENUM. */
export const MOTIVOS_MOVIMIENTO = [
    'orden_compra',
    'factura_compra',
    'produccion_cocina',
    'ajuste_inventario',
    'inventario',
    'devolucion',
    'merma',
    'receta',
    'reverso_produccion',
    'salida_bodega',
    'reverso_salida_bodega',
    'entrada_bodega',
];
export const ETIQUETAS_MOVIMIENTO = {
    orden_compra: {
        label: 'Recepción OC',
        corta: 'Recepción OC',
        emoji: '📦',
        tono: 'compra',
    },
    factura_compra: {
        label: 'Compra factura',
        corta: 'Factura',
        emoji: '🧾',
        tono: 'compra',
    },
    produccion_cocina: {
        label: 'Producción cocina',
        corta: 'Producción',
        emoji: '🍳',
        tono: 'produccion',
    },
    ajuste_inventario: {
        label: 'Ajuste de inventario',
        corta: 'Ajuste',
        emoji: '🎚️',
        tono: 'ajuste',
    },
    inventario: {
        label: 'Inventario',
        corta: 'Inventario',
        emoji: '📊',
        tono: 'ajuste',
    },
    devolucion: {
        label: 'Devolución',
        corta: 'Devolución',
        emoji: '↩️',
        tono: 'reverso',
    },
    merma: { label: 'Merma', corta: 'Merma', emoji: '⚠️', tono: 'merma' },
    receta: { label: 'Receta', corta: 'Receta', emoji: '📋', tono: 'produccion' },
    reverso_produccion: {
        label: 'Reverso de producción',
        corta: 'Rev. producción',
        emoji: '↩️',
        tono: 'reverso',
    },
    salida_bodega: {
        label: 'Salida de bodega',
        corta: 'Salida bodega',
        emoji: '📤',
        tono: 'bodega',
    },
    reverso_salida_bodega: {
        label: 'Reverso de salida',
        corta: 'Rev. salida',
        emoji: '↩️',
        tono: 'reverso',
    },
    entrada_bodega: {
        label: 'Entrada de bodega',
        corta: 'Entrada bodega',
        emoji: '📥',
        tono: 'bodega',
    },
};
/** Etiqueta de un motivo, con salida razonable si llega uno desconocido. */
export const etiquetaMovimiento = (motivo) => ETIQUETAS_MOVIMIENTO[motivo] ?? {
    label: motivo ?? 'Movimiento',
    corta: motivo ?? 'Movimiento',
    emoji: '📝',
    tono: 'ajuste',
};
/**
 * Los motivos que son un reverso de otro movimiento.
 *
 * Se derivan del `tono` para que agregar un motivo de reverso al mapa de
 * arriba alcance: si hubiera una lista suelta, quedaría desincronizada el
 * día que aparezca "reverso_entrada_bodega".
 *
 * Importa porque un reverso es una entrada por su `tipo` —devuelve stock—
 * pero contarlo junto a las compras infla las entradas del período. En el
 * kardex va en su propia tarjeta (PROMPT_N°054).
 */
export const MOTIVOS_REVERSO = MOTIVOS_MOVIMIENTO.filter((m) => ETIQUETAS_MOVIMIENTO[m].tono === 'reverso');
export const esMotivoReverso = (motivo) => MOTIVOS_REVERSO.includes(motivo ?? '');
/**
 * Signo del movimiento según su tipo. Las salidas se guardan en negativo,
 * pero no todas las filas respetan esa convención (las de producción por
 * lote guardan `cantidad = 0`, TD-047), así que el tipo es lo confiable.
 */
export const signoMovimiento = (tipo) => tipo === 'salida' ? -1 : tipo === 'entrada' ? 1 : 0;
